import moment from 'moment'
import { bool, func, string, oneOf, any } from 'prop-types'
import { useEffect, useRef } from 'react'
import { hypernovaIconNames } from '@opallabs/constellation'
import { ThemeProvider } from '@opallabs/hooks'
import { Icon, Switch } from '@opallabs/lapidary'
import Conditional from '../../Conditional'
import DatePickerPopover from '../../Inputs/DatePicker/Popover'
import {
  kuiperClassNames,
  kuiperClassNamesWithTracking,
  renderIcon,
  noop,
} from '../../utils'

import './styles.scss'

const popoverConstraints = [
  {
    popover: 'top center',
    attachment: 'bottom center',
  },
  {
    popover: 'top right',
    attachment: 'bottom center',
  },
  {
    popover: 'top left',
    attachment: 'bottom center',
  },
  {
    popover: 'bottom center',
    attachment: 'top center',
  },
  {
    popover: 'bottom right',
    attachment: 'top center',
  },
  {
    popover: 'bottom left',
    attachment: 'top center',
  },
]

const iconSpan = (icon, onClick) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <span onClick={onClick} className={kuiperClassNames('control-icon', icon)}>
    {renderIcon(icon, { height: 8, width: 8, ariaMode: 'hidden' })}
  </span>
)

const expander = (onExpand) =>
  onExpand ? iconSpan('chevron-e', onExpand) : null

const remover = (onRemove) => (onRemove ? iconSpan('close-sm', onRemove) : null)

const getIcon = (iconName) => (hypernovaIconNames[iconName] ? iconName : 'file')

const reverser = (onReverse) => (
  <Conditional condition={!!onReverse}>
    {iconSpan('chevron-w', onReverse)}
  </Conditional>
)

const adder = (onAdd) => (onAdd ? iconSpan('plus', onAdd) : null)

const popoverOptions = {
  constraintElement: null,
  constraints: popoverConstraints,
}

const renderControlIcon = (glyph) => {
  if (/gem-/.test(glyph)) {
    return <Icon glyph={glyph} />
  }

  return renderIcon(getIcon(glyph), {
    ariaMode: 'hidden',
    height: 14,
    width: 14,
  })
}

const datePicker = (value, onChange, theme) => (
  <DatePickerPopover
    date={moment(value)}
    dateFormat="LL"
    input={true}
    key="datepicker"
    onChangeDate={(date) => onChange(moment(date.due_date).utc().format())}
    popoverOptions={popoverOptions}
    {...{
      theme,
    }}
  />
)

const Filter = ({
  color,
  disabled,
  divider,
  highlighted,
  icon,
  id,
  indented,
  onAdd,
  onChange,
  onClick,
  onDisable,
  onExpand,
  onMouseMove = noop,
  onRemove,
  onReverse,
  selected,
  title,
  type,
  value,
}) => {
  const filterRef = useRef(null)

  useEffect(() => {
    if (filterRef.current && onExpand) {
      filterRef.current.ariaExpanded = Boolean(onReverse)
    }
  }, [filterRef, onExpand, onReverse])

  if (!id) return null

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      ref={filterRef}
      key={id}
      role="button"
      tabIndex={0}
      className={kuiperClassNamesWithTracking(
        'filter',
        `${id.split('-')[0]}-filter`,
        {
          expandable: onExpand,
          removable: onRemove,
          reversible: onReverse,
          clickable: onClick,
          disabled,
          divider,
          highlighted,
          indented,
          selected,
          date: onChange && type === 'date',
          tracking: `${id.split('-')[0]}-filter`,
        }
      )}
      onClick={onClick}
      onMouseMove={onMouseMove.bind(this, id)}
    >
      {reverser(onReverse)}

      <span className={kuiperClassNames('title')}>
        <span
          className={kuiperClassNames('filter-icon', `filter-icon--${icon}`)}
          style={{
            fill: color,
            color,
          }}
        >
          {renderControlIcon(icon)}
        </span>
        <span className={kuiperClassNames('title-text')} aria-live="polite">
          {title}
        </span>
      </span>

      {onChange && type === 'date'
        ? datePicker(value, onChange.bind(this, id), 'light')
        : null}
      <div className={kuiperClassNames('spacer')} />
      {expander(onExpand)}
      {remover(onRemove)}
      {adder(onAdd)}
      <Conditional condition={Boolean(onDisable)}>
        <div className={kuiperClassNames('hover-effect')}>
          <ThemeProvider theme="light">
            <Switch
              checked={!disabled}
              checkedLabel="Enabled"
              onToggle={(checked) => onDisable(!checked)}
              uncheckedLabel="Disabled"
            />
          </ThemeProvider>
        </div>
      </Conditional>
    </div>
  )
}
Filter.displayName = 'Kuiper: Filter ← FilterBar'

Filter.propTypes = {
  color: string,
  disabled: bool,
  divider: bool,
  highlighted: bool,
  icon: string,
  id: string,
  indented: bool,
  onAdd: func,
  onChange: func,
  onClick: func,
  onDisable: func,
  onExpand: func,
  onMouseMove: func,
  onRemove: func,
  onReverse: func,
  selected: bool,
  theme: oneOf(['dark', 'light']),
  title: string,
  type: oneOf(['date']),
  value: any,
}

export default Filter
